import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_JOINT } from '../../../lib/constants';
import { TJoint } from '../../../lib/types';
import { useCertificateId } from '../../../lib/util';
import { CertificateTypeEnum } from '../../../graph-queries/__generated__/graphql';

const JointForm: FC<{
  certificateType: string;
  defaultValues?: TJoint;
  onSubmit: (joint: TJoint) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ certificateType, defaultValues, onSubmit, type, onClose, isLoading }) => {
  const certificateId = useCertificateId();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string>;

    const joint: TJoint = {
      ...EMPTY_JOINT,
      ...inputData,
      certificateId,
      jointId: parseInt(inputData.jointId),
      supervisedAssembly: inputData.supervisedAssembly === 'required' ? true : false,
    };
    if (defaultValues?.jointId) joint.jointId = defaultValues.jointId;

    // always set supervisedAssembly to true for SBT certificates
    if (certificateType === CertificateTypeEnum.Sbt) {
      joint.supervisedAssembly = true;
    }

    onSubmit(joint)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-4">
        <FormGroup>
          <Label for="joint-number">Joint Number</Label>
          <Input
            type="text"
            name="jointNumber"
            id="joint-number"
            required
            defaultValue={defaultValues?.jointNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-8">
        <FormGroup>
          <Label for="additional-details">Additional Details</Label>
          <Input
            type="text"
            name="additionalDetails"
            id="additional-details"
            required
            defaultValue={defaultValues?.additionalDetails || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="tag-id">Tag ID</Label>
          <Input type="text" name="tagId" id="tag-id" defaultValue={defaultValues?.tagId || ''} />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="p&id-no">P&ID No</Label>
          <Input type="text" name="pIdNo" id="p&id-no" defaultValue={defaultValues?.pIdNo || ''} />
        </FormGroup>
      </div>

      {certificateType !== CertificateTypeEnum.Sbt && (
        <>
          <div className="col-md-6">
            <FormGroup>
              <Label for="flange-size">Flange Size</Label>
              <Input
                type="text"
                name="flangeSize"
                id="flange-size"
                defaultValue={defaultValues?.flangeSize || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="rating">Rating</Label>
              <Input
                type="text"
                name="rating"
                id="rating"
                defaultValue={defaultValues?.rating || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="bolt-diameter">Bolt Diameter</Label>
              <Input
                type="text"
                name="boltDiameter"
                id="bolt-diameter"
                defaultValue={defaultValues?.boltDiameter || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="bolt-quantity">Bolt Quantity</Label>
              <Input
                type="text"
                name="boltQuantity"
                id="bolt-quantity"
                defaultValue={defaultValues?.boltQuantity || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="bolt-length">Bolt Length</Label>
              <Input
                type="text"
                name="boltLength"
                id="bolt-length"
                defaultValue={defaultValues?.boltLength || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="bolt-material">Bolt Material</Label>
              <Input
                type="text"
                name="boltMaterial"
                id="bolt-material"
                defaultValue={defaultValues?.boltMaterial || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="gasket-type">Gasket Type</Label>
              <Input
                type="text"
                name="gasketType"
                id="gasket-type"
                defaultValue={defaultValues?.gasketType || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="torque-value">Torque Value</Label>
              <Input
                type="text"
                name="torqueValue"
                id="torque-value"
                defaultValue={defaultValues?.torqueValue || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="hydraulic-tool-ref">Hydraulic Tool Ref</Label>
              <Input
                type="text"
                name="hydraulicToolRef"
                id="hydraulic-tool-ref"
                defaultValue={defaultValues?.hydraulicToolRef || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="hydraulic-tool-pass">Hydraulic Tool Pass</Label>
              <Input
                type="text"
                name="hydraulicToolPass"
                id="hydraulic-tool-pass"
                defaultValue={defaultValues?.hydraulicToolPass || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="hydraulic-tool-pump-pressure">Hydraulic Tool Pump Pressure</Label>
              <Input
                type="text"
                name="hydraulicToolPumpPressure"
                id="hydraulic-tool-pump-pressure"
                defaultValue={defaultValues?.hydraulicToolPumpPressure || ''}
              />
            </FormGroup>
          </div>
        </>
      )}
      {certificateType !== CertificateTypeEnum.Sbt && (
        <>
          <div className="col-md-6">
            <FormGroup>
              <Label className="x5-me-6">Supervised Assembly</Label>
              <div className="d-flex">
                <FormGroup check className="me-3" inline>
                  <Input
                    required
                    id="sa-1"
                    name="supervisedAssembly"
                    type="radio"
                    value="required"
                    defaultChecked={defaultValues?.supervisedAssembly === true}
                  />
                  <Label check for="sa-1" className="ms-1">
                    Yes
                  </Label>
                </FormGroup>
                <FormGroup check className="me-0" inline>
                  <Input
                    id="sa-2"
                    name="supervisedAssembly"
                    type="radio"
                    value="notRequired"
                    defaultChecked={defaultValues?.supervisedAssembly === false}
                  />
                  <Label check for="sa-2" className="ms-1">
                    No
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>
          </div>
        </>
      )}

      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new joint
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default JointForm;
